import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import LoadingSpinner from "../../AdminMetricDashboard/LoadingIndicator/LoadingSpinner";
import { csvDownload } from "../../AdminMetricDashboard/ExportCsv";
import { getJobMetricsCountData } from "../../../../callable-cloud-functions/cloudFunctions";
import toast from "react-hot-toast";
import ToastAlert from "../../../custom-ui/toast-alert/ToastAlert";
import DownloadIconWithLabel from "../../AdminMetricDashboard/DownLoadIcon";
import { Download } from "lucide-react";

const COLORS = {
  clicks: "#0c485e",
  views: "#3aa4c4",
};

const JobInteractionsChart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getJobMetricsCountData();
      if (Array.isArray(res.data)) {
        setData(res.data);
      } else {
        setError("Invalid data format");
      }
    } catch (err) {
      setError("Failed to load chart data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formattedData = useMemo(() => {
    if (!data.length) return [];

    return data.map((d) => {
      const date = new Date(d.month + "-01");
      return {
        month: date.toLocaleString("default", { month: "short" }),
        clicks: d.applyCount,
        views: d.viewCount,
      };
    });
  }, [data]);

  /** CSV download */
  const handleDownload = () => {
    if (!data.length) {
      toast.error("No data available.");
      return;
    }
    const downloadData = data.map((d) => ({
      Month: d.month,
      Views: d.viewCount,
      Clicks: d.applyCount,
    }));

    csvDownload(downloadData, "Job_Board_Metrics_Last_6_Months");
  };

  return (
    <>
      <div className="mt-6 border border-gray-200 rounded-lg bg-white p-8">
        <div className="flex w-full justify-between items-center px-1 mb-4">
          <div>
            <h2 className="text-2xl text-instant-teams-blue-Main font-bold text-foreground">
              Job Board Metrics - Last 6 Months
            </h2>
            <h2 className="text-lg text-gray-400">Monthly Clicks and Views</h2>
          </div>
          <button
            onClick={handleDownload}
            className="flex items-center border text-instant-teams-blue-Main  border-gray-300 hover:bg-gray-100 px-6 py-2 text-lg rounded-md text-sm"
          >
            <Download className="mr-2 h-4 w-4" />
            Download
          </button>
        </div>

        <div className="bg-white  ">
          {loading ? (
            <div className="flex justify-center items-center h-60">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="text-red-500 text-center h-60 flex items-center justify-center">
              {error}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={formattedData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend verticalAlign="bottom" />

                <Bar dataKey="clicks" fill={COLORS.clicks} name="Clicks" />
                <Bar dataKey="views" fill={COLORS.views} name="Views" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      <ToastAlert />
    </>
  );
};

export default JobInteractionsChart;
